import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/content";
import { scrollToHash } from "../utils/scroll";
import { EASE } from "./motion";
import { Wordmark as Mark } from "./Wordmark";

const Wordmark = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center">
    <Mark />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const go = (to) => {
    setOpen(false);
    const [path, hash] = to.split("#");
    if (hash) {
      const target = path || "/";
      if (location.pathname !== target) navigate(target, { state: { scrollTo: hash } });
      else scrollToHash(hash);
    } else {
      navigate(to);
    }
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-white/10 bg-[#150E1C]/85 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-[1500px] items-center justify-between px-6 sm:px-10 lg:px-16">
          <Wordmark />
          <nav className="hidden items-center gap-4 xl:flex 2xl:gap-8" data-testid="nav-menu">
            {NAV_LINKS.map((l) => (
              <button
                key={l.label}
                onClick={() => go(l.to)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|é/g, (m) => (m === "é" ? "e" : "-"))}`}
                className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.12em] text-white/70 2xl:text-[11px] 2xl:tracking-[0.18em] transition-colors hover:text-neon"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => go("/rendez-vous")}
              data-testid="nav-cta-parler-de-mon-projet"
              className="hidden items-center gap-2 whitespace-nowrap bg-neon px-5 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-ink transition-colors hover:bg-neon-light md:inline-flex 2xl:px-6 2xl:text-[11px] 2xl:tracking-[0.2em]"
            >
              Parler de mon projet <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-burger"
              aria-label="Ouvrir le menu"
              className="grid h-11 w-11 place-items-center border border-white/20 text-cream xl:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#150E1C] px-6 py-6"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Wordmark />
              <button
                onClick={() => setOpen(false)}
                aria-label="Fermer le menu"
                data-testid="nav-close"
                className="grid h-11 w-11 place-items-center border border-white/20 text-cream"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-16 flex flex-col gap-6">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.08 + i * 0.06, ease: EASE }}
                  onClick={() => go(l.to)}
                  className="text-left font-serif text-4xl text-cream transition-colors hover:text-neon"
                  data-testid={`nav-mobile-link-${i}`}
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <button
              onClick={() => go("/rendez-vous")}
              data-testid="nav-mobile-cta"
              className="mt-auto inline-flex items-center justify-center gap-3 bg-neon px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-ink"
            >
              Parler de mon projet <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
